/**
 * Identity key material (F6, ADR-12). Live keys are NON-extractable WebCrypto
 * handles (sign.ts: "non-extractable hardening is F6's client policy"); the
 * export bundle is the manual-recovery file (mock register) and is only
 * obtainable at generation time. did:beacon == hash(GENESIS signing key) — a
 * rotated identity's bundle still imports; the chain, not the bundle, proves
 * key currency (foldIdentity).
 */
import {
  deriveDidBeacon,
  didKeyFromJwk,
  generateEncryptionKeypair,
  generateSigningKeypair,
  type Jwk,
} from "@beacon/crypto";
import type { webcrypto } from "node:crypto";

type CryptoKey = webcrypto.CryptoKey;
type KeyUsage = webcrypto.KeyUsage;

export interface IdentityKeys {
  did: string;
  signingKey: string;            // did:key of the CURRENT signing key
  encryptionKey: string;         // did:key of the current HPKE key
  signingPrivate: CryptoKey;     // non-extractable
  encryptionPrivate: CryptoKey;  // non-extractable
}

export interface IdentityBundle {
  v: 1;
  kind: "beacon-identity-export";
  did: string;
  signingKey: string;
  signingJwk: Jwk;               // private (d,x,y)
  encryptionKey: string;
  encryptionJwk: Jwk;            // private (d,x,y)
}

async function importNonExtractable(
  jwk: Jwk,
  alg: "ECDSA" | "ECDH",
  usages: KeyUsage[],
  label: "signing" | "encryption",
): Promise<CryptoKey> {
  const { key_ops: _o, ext: _e, ...clean } = jwk;
  try {
    return await crypto.subtle.importKey(
      "jwk", clean, { name: alg, namedCurve: "P-256" }, false, usages,
    );
  } catch (cause) {
    throw new Error(`identity: ${label} key import failed`, { cause });
  }
}

export async function createIdentity(): Promise<{ keys: IdentityKeys; bundle: IdentityBundle }> {
  const sig = await generateSigningKeypair();
  const enc = await generateEncryptionKeypair();
  const signingJwk = (await crypto.subtle.exportKey("jwk", sig.privateKey)) as Jwk;
  const encryptionJwk = (await crypto.subtle.exportKey("jwk", enc.privateKey)) as Jwk;
  const signingKey = didKeyFromJwk(sig.publicJwk as { x: string; y: string });
  const encryptionKey = didKeyFromJwk(enc.publicJwk as { x: string; y: string });
  const did = await deriveDidBeacon(signingKey);
  const bundle: IdentityBundle = {
    v: 1, kind: "beacon-identity-export", did, signingKey, signingJwk, encryptionKey, encryptionJwk,
  };
  return { keys: await importIdentityBundle(bundle), bundle };
}

export async function importIdentityBundle(b: IdentityBundle): Promise<IdentityKeys> {
  if (b.v !== 1 || b.kind !== "beacon-identity-export") {
    throw new Error("identity: unknown bundle version/kind");
  }
  // Each did:key must match its private JWK. The did:beacon is deliberately NOT
  // re-derived from b.signingKey: it hashes the GENESIS key, and a rotated
  // identity's bundle legitimately carries a newer signingKey (header comment).
  if (didKeyFromJwk(b.signingJwk as { x: string; y: string }) !== b.signingKey) {
    throw new Error("identity: bundle signingKey/JWK mismatch");
  }
  if (didKeyFromJwk(b.encryptionJwk as { x: string; y: string }) !== b.encryptionKey) {
    throw new Error("identity: bundle encryptionKey/JWK mismatch");
  }
  return {
    did: b.did,
    signingKey: b.signingKey,
    encryptionKey: b.encryptionKey,
    signingPrivate: await importNonExtractable(b.signingJwk, "ECDSA", ["sign"], "signing"),
    encryptionPrivate: await importNonExtractable(b.encryptionJwk, "ECDH", ["deriveBits"], "encryption"),
  };
}
